import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { ApplyApi } from '@apply/api/src/api/ApplyApi';
import { applicationInsights } from '../../ApplicationInsights';
import { useNavigate } from 'react-router';
import { GenericAlertPopup } from '@apply/components';
import { useWaitOverlayStore } from '@apply/components';

const applyApi = new ApplyApi(applicationInsights);

enum QueryKeys {
    UnverifiedExperience = 'unverifiedExperience',
    VerifiedExperience = 'verifiedExperience',
    UserExperience = 'userExperience',
}

export function useUnverifiedExperienceQuery() {
    return useQuery({
        queryKey: [QueryKeys.UnverifiedExperience],
        queryFn: async () => {
            const response = await applyApi.getUnverifiedUserExperiences();
            if (response.statusCode !== 200 || !response.result) {
                const errorMsg =
                    response.errors?.length > 0 ? response.errors.join(', ') : 'Failed to load User Unverified Experience data';
                throw new Error(errorMsg);
            }
            return response.result;
        },
    });
}

export function useVerifiedExperienceQuery() {
    return useQuery({
        queryKey: [QueryKeys.VerifiedExperience],
        queryFn: async () => {
            const response = await applyApi.getVerifiedUserExperiences();
            if (response.statusCode !== 200 || !response.result) {
                const errorMsg =
                    response.errors?.length > 0 ? response.errors.join(', ') : 'Failed to load User Verified Experience data';
                throw new Error(errorMsg);
            }
            return response.result;
        },
    });
}

export function useRefreshUserExperience() {
    const queryClient = useQueryClient();
    return () => {
        queryClient.invalidateQueries({ queryKey: [QueryKeys.UnverifiedExperience] });
        queryClient.invalidateQueries({ queryKey: [QueryKeys.VerifiedExperience] });
    };
}

// Fetch single experience by id (uses new ApplyApi method)
export function useGetUserExperienceQuery(experienceId?: number | string) {
    const navigate = useNavigate();
    const { setWaiting } = useWaitOverlayStore();

    return useQuery({
        queryKey: ['userExperience', experienceId],
        enabled: !!experienceId,
        queryFn: async () => {
            const response = await applyApi.getUserExperienceItem(Number(experienceId));
            if (response.statusCode !== 200 || !response.result) {
                const errorMsg = 'Unable to access this Project at this time.';
                setWaiting(false);
                // Show popup
                GenericAlertPopup({
                    title: 'Error',
                    children: errorMsg,
                    width: '350px',
                    handleClose: () => navigate('/experience'),
                });

                // Throw to mark query as failed
                throw new Error(errorMsg);
            }
            return response.result;
        },
    });
}

// Save (create or update) user experience (uses new ApplyApi method)
export function useSaveUserExperienceMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: any) => {
            const response = await applyApi.saveUserExperienceItem(payload);
            if (response.statusCode !== 200 || !response.result) {
                const errorMsg = response.errors?.length > 0 ? response.errors.join(', ') : 'Failed to save User Experience';
                throw new Error(errorMsg);
            }
            return response.result;
        },
        onSuccess: (data, variables: any) => {
            queryClient.invalidateQueries({ queryKey: [QueryKeys.UnverifiedExperience] });
            queryClient.invalidateQueries({ queryKey: [QueryKeys.VerifiedExperience] });
            if (variables?.id) {
                queryClient.invalidateQueries({ queryKey: [QueryKeys.UserExperience, variables.id] });
            }
        },
    });
}

// Validate user experience (uses new ApplyApi method)
export function useValidateUserExperienceMutation() {
    return useMutation({
        mutationFn: async (payload: any) => {
            const response = await applyApi.validateUserExperience(payload);

            if (response.statusCode !== 200) {
                throw new Error('Failed to validate User Experience');
            }

            return response.result;
        },
    });
}

export function useDeleteUserExperienceMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: number) => {
            const response = await applyApi.deleteUserExperienceItem(id);
            if (response.statusCode !== 200) {
                const errorMsg = response.errors?.length > 0 ? response.errors.join(', ') : 'Failed to delete User Experience';
                throw new Error(errorMsg);
            }
            return response.result;
        },
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: [QueryKeys.UnverifiedExperience] });
            queryClient.invalidateQueries({ queryKey: [QueryKeys.VerifiedExperience] });
            queryClient.invalidateQueries({ queryKey: [QueryKeys.UserExperience, id] });
        },
    });
}
